import { React, useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Edit, Delete } from '@mui/icons-material';
import Progress from '../Progress';
import AddTDSConfigurationForm from '../forms/AddTDSConfigurationForm';
import Popup from '../Popup';
import Notification from '../Notification';
import ConfirmDialog from '../ConfirmDialog';

const TDSConfigurationTable = forwardRef((props, ref) => {
    const [openPopup, setOpenPopup] = useState(false);
    const [fetch_id, setFetchId] = useState(0);
    const [tdsConfigs, setTdsConfigs] = useState([]);
    const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
    const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', subTitle: '' });
    const [loading, setLoading] = useState(true);
    const selectedFinancialYear = "2025-2026"; // Hard coded financial year

    const navigate = useNavigate();

    async function getAllTDSConfigs() {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/configure/get-all-tax-ranges/${selectedFinancialYear}`);
            // Handle nested response structure: response.data.data
            if (response.data && response.data.data) {
                setTdsConfigs(response.data.data);
            } else {
                setTdsConfigs([]);
            }
        } catch (e) {
            if (e.response && e.response.status === 401) {
                setNotify({
                    isOpen: true,
                    message: 'Session Expired!',
                    type: 'error'
                });
                setTimeout(() => {
                    navigate('/');
                }, 1000);
            } else if (e.response && e.response.status === 404) {
                // API endpoint might not exist yet, set empty array
                setTdsConfigs([]);
            } else {
                // Other errors, set empty array
                setTdsConfigs([]);
            }
        }
        setLoading(false);
    }

    // Expose refresh function via ref
    useImperativeHandle(ref, () => ({
        refreshTable: getAllTDSConfigs
    }));

    useEffect(() => {
        getAllTDSConfigs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Refresh when refreshKey changes
    useEffect(() => {
        if (props.refreshKey && props.refreshKey > 0) {
            getAllTDSConfigs();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.refreshKey]);

    const handleDelete = async (delete_id) => {
        setConfirmDialog({
            ...confirmDialog,
            isOpen: false
        });
        try {
            const response = await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/configure//delete-tax-range/` + delete_id);
            if (response.status === 200) {
                setNotify({
                    isOpen: true,
                    message: 'Successfully Deleted!',
                    type: 'success'
                });
                getAllTDSConfigs();
            }
        } catch (e) {
            if (e.response && e.response.status === 422) {
                setNotify({
                    isOpen: true,
                    message: e.response.data.error_message || 'Failed to delete',
                    type: 'error'
                });
            }
            if (e.response && e.response.status === 401) {
                setNotify({
                    isOpen: true,
                    message: 'Session Expired!',
                    type: 'error'
                });
                setTimeout(() => {
                    navigate('/');
                }, 1000);
            }
        }
    };

    return (
        <>
            {loading ? <Progress /> :
                <div style={{
                    width: '100%',
                    padding: '10px 0',
                    boxSizing: 'border-box'
                }}>
                    <div style={{
                        width: '100%',
                        backgroundColor: '#fff',
                        borderRadius: '8px',
                        overflow: 'hidden'
                    }}>
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            marginLeft: "80px"
                        }} className="table">
                            <thead className="table-dark">
                                <tr>
                                    <th scope="col" style={{ padding: '12px', textAlign: 'left' }}>Start Range - End Range</th>
                                    <th scope="col" style={{ padding: '12px', textAlign: 'center' }}>Tax Rate (%)</th>
                                    <th scope="col" style={{ padding: '12px', textAlign: 'center' }}>Financial Year</th>
                                    <th scope="col" style={{ padding: '12px', textAlign: 'center', width: '200px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tdsConfigs.length > 0 ? (
                                    tdsConfigs.map((tdsConfig, i) => {
                                        const startRange = tdsConfig.min_amount || tdsConfig.startRange || tdsConfig.start_range || tdsConfig.StartRange || 0;
                                        const endRange = tdsConfig.max_amount || tdsConfig.endRange || tdsConfig.end_range || tdsConfig.EndRange || 0;
                                        const taxRate = tdsConfig.tax_rate || tdsConfig.taxRate || tdsConfig.tax_rate || tdsConfig.TaxRate || 0;
                                        const financialYear = tdsConfig.financial_year || tdsConfig.effectiveDate || tdsConfig.effective_date || tdsConfig.EffectiveDate || '';
                                        const configId = tdsConfig.tax_details_id || tdsConfig.id || tdsConfig.tdsConfigId || tdsConfig.TDSConfigId || tdsConfig.tax_range_id;

                                        return (
                                            <tr key={configId || i} style={{ borderBottom: '1px solid #dee2e6' }}>
                                                <td style={{ padding: '12px', textAlign: 'left' }}>{Number(startRange).toLocaleString('en-IN')} - {Number(endRange).toLocaleString('en-IN')}</td>
                                                <td style={{ padding: '12px', textAlign: 'center' }}>{taxRate}%</td>
                                                <td style={{ padding: '12px', textAlign: 'center' }}>{financialYear}</td>
                                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-primary btn-sm"
                                                        onClick={() => {
                                                            setFetchId(configId);
                                                            setOpenPopup(true);
                                                        }}
                                                        style={{ marginRight: '8px' }}
                                                    >
                                                        <Edit /> Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-danger btn-sm"
                                                        onClick={() =>
                                                            setConfirmDialog({
                                                                isOpen: true,
                                                                title: 'Are you sure to delete this record?',
                                                                subTitle: "You can't undo this operation",
                                                                onConfirm: () => {
                                                                    handleDelete(configId);
                                                                },
                                                            })
                                                        }
                                                    >
                                                        <Delete /> Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="4" style={{ textAlign: "center", padding: "40px", color: '#6c757d' }}>
                                            No TDS configurations found. Click "Add TDS Configuration" to create one.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <Popup
                        title={fetch_id ? "Edit TDS Configuration" : "Add TDS Configuration"}
                        openPopup={openPopup}
                        setOpenPopup={(open) => {
                            setOpenPopup(open);
                            if (!open) {
                                setFetchId(0); // Reset fetch_id when popup closes
                            }
                        }}
                    >
                        <AddTDSConfigurationForm
                            fetch_id={fetch_id}
                            onRefresh={getAllTDSConfigs}
                            setOpenPopup={(open) => {
                                setOpenPopup(open);
                                if (!open) {
                                    setFetchId(0); // Reset fetch_id when popup closes
                                }
                            }}
                        />
                    </Popup>
                </div>}
            <Notification
                notify={notify}
                setNotify={setNotify} />
            <ConfirmDialog
                confirmDialog={confirmDialog}
                setConfirmDialog={setConfirmDialog} />
        </> 
    );
});

export default TDSConfigurationTable;

